import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useQueryClient } from '@tanstack/react-query';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import { CircleAlert, Lock } from 'lucide-react-native';
import { useEffect, useRef, useState } from 'react';
import { Modal as RNModal } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { twJoin } from 'tailwind-merge';

import { AwayCalendar } from '@/components/account/AwayCalendar';
import { Button, ButtonText } from '@/components/ui/button';
import { Divider } from '@/components/ui/divider';
import { Heading } from '@/components/ui/heading';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Modal, ModalBackdrop, ModalBody, ModalContent } from '@/components/ui/modal';
import { Text } from '@/components/ui/text';
import { View } from '@/components/ui/view';
import { VStack } from '@/components/ui/vstack';
import { useDeleteAwayScheduleBlock, useReplaceAwayPeriod } from '@/hooks/useAwaySchedule';
import { useToaster } from '@/hooks/useToaster';
import type { AwayScheduleBlock } from '@/services/getAwayScheduleV2';
import { getCurrentUserV2QueryOptions } from '@/services/getCurrentUserV2';
import { setUserStatusV2 } from '@/services/setUserStatusV2';
import type { components } from '@primum/api-types';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import {
  trackAwayPeriodRemoved,
  trackAwayPeriodSaved,
  trackAwayPeriodSaveFailed,
  trackAwaySheetViewed,
} from '@/util/analyticsEvents';
import {
  applyDayTap,
  countAwayDays,
  formatAwayDay,
  getBlockedDayReason,
  getDayAfter,
  MAX_AWAY_DAYS,
  validateAwayRange,
  type RangeSelection,
} from '@/util/awaySchedule';
import { ApiError } from '@/util/fetchClient';

type AwayScheduleSheetProps = {
  isOpen: boolean;
  onClose: () => void;
  userId?: string;
  today: string;
  /** The one period the UI shows, see `getAwayPeriod`. */
  period?: AwayScheduleBlock;
  /** Every other block the expert has, which a new range must not overlap. */
  otherBlocks: AwayScheduleBlock[];
  /** Away through the old status switch, with no end date. */
  legacyAway: boolean;
  /** Opens support, as the Account screen's Contact support row does. The sheet closes first. */
  onContactSupport: () => void;
};

const GENERIC_SAVE_ERROR = 'Couldn’t save. Try again.';

type AwayScheduleErrorCode = NonNullable<
  components['schemas']['AwayScheduleErrorResponse']['code']
>;

/**
 * The words the expert sees for each code the server names. The server's own
 * messages are for logs: they carry raw dates and ids, so only the code is used.
 */
const ERROR_COPY: Record<AwayScheduleErrorCode, string> = {
  invalid_date_range: 'Your last day can’t be before your first day.',
  start_in_past: 'Your first day can’t be in the past.',
  block_too_long: `Time away can last up to ${MAX_AWAY_DAYS} days. Choose an earlier last day.`,
  overlaps_existing: 'These dates overlap another time away. Choose different dates.',
  not_found: 'That time away was already removed.',
};

const ERROR_CODES = Object.keys(ERROR_COPY) as AwayScheduleErrorCode[];

/** The server's code when it is one the contract names, so a stray string never becomes a bucket. */
function readErrorCode(body: { code?: unknown } | null): AwayScheduleErrorCode | undefined {
  return ERROR_CODES.find((code) => code === body?.code);
}

/**
 * What to show and what to report for a failed save. A missing period is also a
 * bare 404 from a server that predates the codes. The reported code falls back to
 * the HTTP status or `network` so failures stay distinguishable.
 */
function describeSaveError(error: unknown): { message: string; code: string } {
  if (!(error instanceof ApiError)) {
    return { message: GENERIC_SAVE_ERROR, code: 'network' };
  }

  const code = readErrorCode(error.body as { code?: unknown } | null);

  if (code) {
    return { message: ERROR_COPY[code], code };
  }

  if (error.status === 404) {
    return { message: ERROR_COPY.not_found, code: 'not_found' };
  }

  return { message: GENERIC_SAVE_ERROR, code: `http_${error.status}` };
}

function getRemoveLabel({
  legacyAway,
  isStarted,
}: {
  legacyAway: boolean;
  isStarted: boolean;
}): string {
  if (legacyAway) {
    return 'End now';
  }

  return isStarted ? 'End time away' : 'Cancel time away';
}

const WELCOME_BACK = 'Welcome back. You’re accepting inquiries again.';

function toSelection(period?: AwayScheduleBlock): RangeSelection {
  return { start: period?.startDate ?? null, end: period?.endDate ?? null };
}

/**
 * The only place an expert sets time away, as a centered modal on web and a
 * full-height screen on native around one shared body. The months scroll, so a sheet's drag
 * would compete with them. A new period opens on
 * the calendar. An existing one opens on a summary with Change dates and a
 * button to end it, so the common action, ending early, is one tap. Save
 * replaces the shown block in place (see `replaceAwayPeriodV2`), and ending or
 * cancelling deletes it with no confirmation, because undoing either is one
 * more save. An expert still on the old no-end-date Away status can end it from
 * the same button, since the switch that used to clear it is gone. Saving a
 * period clears that status on the server, so the sheet only reads it back.
 */
export function AwayScheduleSheet({
  isOpen,
  onClose,
  userId,
  today,
  period,
  otherBlocks,
  legacyAway,
  onContactSupport,
}: AwayScheduleSheetProps) {
  const queryClient = useQueryClient();

  const { bottom, top } = useSafeAreaInsets();

  const { showSuccess } = useToaster();

  const replace = useReplaceAwayPeriod();

  const remove = useDeleteAwayScheduleBlock();

  const [selection, setSelection] = useState<RangeSelection>(toSelection(period));

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  /** Why the last tapped day was refused, shown until the next accepted tap. */
  const [blockedNote, setBlockedNote] = useState<string | null>(null);

  const [isBusy, setIsBusy] = useState(false);

  /** An existing period opens on its summary, and the calendar waits behind Change dates. */
  const [isEditingDates, setIsEditingDates] = useState(!period && !legacyAway);

  /**
   * Starts from the saved period each time the sheet opens. This runs while
   * rendering, not in an effect, so the first frame of a reopened sheet never
   * shows the last session's calendar. A background refetch cannot wipe a pick,
   * because only the open flag triggers it.
   */
  const [wasOpen, setWasOpen] = useState(isOpen);

  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);

    if (isOpen) {
      setSelection(toSelection(period));

      setErrorMessage(null);

      setBlockedNote(null);

      setIsEditingDates(!period && !legacyAway);
    }
  }

  useEffect(() => {
    if (isOpen) {
      trackAwaySheetViewed({
        hasExistingPeriod: !!period,
        isCurrent: !!period && period.startDate <= today && today <= period.endDate,
      });
    }
    // Report only when the sheet opens, so a background refetch is not a second view.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const restoreFocusTo = useRef<HTMLElement | null>(null);

  // The web modal does not hand focus back to what opened it, so a keyboard
  // user would land on the page body after closing. Remember the opener and
  // return to it once the modal has unmounted.
  useEffect(() => {
    if (!isWeb) {
      return undefined;
    }

    if (isOpen) {
      restoreFocusTo.current = document.activeElement as HTMLElement | null;

      return undefined;
    }

    const opener = restoreFocusTo.current;

    restoreFocusTo.current = null;

    const timer = setTimeout(() => opener?.focus?.(), 0);

    return () => clearTimeout(timer);
  }, [isOpen]);

  const isStarted = !!period && period.startDate <= today;

  const hasPeriodOrLegacy = !!period || legacyAway;

  const { start, end } = selection;

  const isEditing = !!period;

  const showSummary = hasPeriodOrLegacy && !isEditingDates;

  const isUnchanged = !!period && start === period.startDate && end === period.endDate;

  const refreshUser = () =>
    queryClient.invalidateQueries({
      queryKey: getCurrentUserV2QueryOptions(userId).queryKey,
    });

  /**
   * A ref, not state, because two taps can land before a re-render, and a state
   * flag would let both through to send two requests.
   */
  const inFlight = useRef(false);

  const handleSave = async () => {
    if (!start || !end || inFlight.current) {
      return;
    }

    inFlight.current = true;

    try {
      const mode = period ? 'edited' : 'new';

      const validation = validateAwayRange(
        { startDate: start, endDate: end },
        otherBlocks,
        today,
        period
      );

      if (!validation.valid) {
        setErrorMessage(validation.message);

        trackAwayPeriodSaveFailed({ mode, errorCode: validation.code });

        return;
      }

      setErrorMessage(null);

      setIsBusy(true);

      try {
        const saved = await replace.mutateAsync({
          previous: period,
          next: { startDate: start, endDate: end },
        });

        trackAwayPeriodSaved({
          mode,
          days: countAwayDays({ startDate: start, endDate: end }),
          startsInDays: differenceInCalendarDays(parseISO(start), parseISO(today)),
          isCurrent: start <= today,
        });

        // The server clears an old Away status when it saves a period, so the
        // row and banner read the status back rather than assuming it.
        if (legacyAway) {
          await refreshUser();
        }

        const shownStart = saved?.startDate ?? start;

        const shownEnd = saved?.endDate ?? end;

        showSuccess({
          title: `Away ${formatAwayDay(shownStart, today)} to ${formatAwayDay(shownEnd, today)}.`,
        });

        onClose();
      } catch (error) {
        const { message, code } = describeSaveError(error);

        setErrorMessage(message);

        if (code === 'not_found') {
          setSelection(toSelection(undefined));
        }

        trackAwayPeriodSaveFailed({ mode, errorCode: code });
      } finally {
        setIsBusy(false);
      }
    } finally {
      inFlight.current = false;
    }
  };

  const handleRemove = async () => {
    if (inFlight.current) {
      return;
    }

    inFlight.current = true;

    setErrorMessage(null);

    setIsBusy(true);

    try {
      if (legacyAway) {
        await setUserStatusV2({ status: '1' });

        await refreshUser();
      }

      if (period) {
        await remove.mutateAsync(period.id);
      }

      trackAwayPeriodRemoved({
        kind: isStarted || legacyAway ? 'ended_early' : 'cancelled_upcoming',
      });

      showSuccess({ title: isStarted || legacyAway ? WELCOME_BACK : 'Time away removed.' });

      onClose();
    } catch (error) {
      const { message, code } = describeSaveError(error);

      if (code === 'not_found') {
        // Someone else already removed it, which is what was asked for.
        showSuccess({ title: 'Time away removed.' });

        onClose();
      } else {
        setErrorMessage(message);
      }
    } finally {
      setIsBusy(false);

      inFlight.current = false;
    }
  };

  let summary = 'Tap the first day of your time away.';

  // Name the start here because, once the calendar pages past its month, nothing else shows it.
  if (start && !end) {
    summary = `Away from ${formatAwayDay(start, today)}. Now tap your last day away.`;
  }

  if (start && end && isUnchanged) {
    summary = isStarted
      ? 'Tap a new last day to change when you’re back.'
      : 'Tap new dates to change your time away.';
  } else if (start && end) {
    const days = countAwayDays({ startDate: start, endDate: end });

    const range =
      start === end
        ? formatAwayDay(start, today)
        : `${formatAwayDay(start, today)} to ${formatAwayDay(end, today)}`;

    summary = `${range}, ${days} ${days === 1 ? 'day' : 'days'} away. Back ${formatAwayDay(getDayAfter(end), today)}.`;
  }

  const summaryHeadline = (() => {
    if (!period) {
      return 'Away with no end date.';
    }

    if (isStarted) {
      return `Away through ${formatAwayDay(period.endDate, today)}.`;
    }

    return `Away ${formatAwayDay(period.startDate, today)} to ${formatAwayDay(period.endDate, today)}.`;
  })();

  const summaryContent = (
    <>
      <Heading size="h4-lg">Time away</Heading>

      <Text className="mt-3 text-base text-typography-900">{summaryHeadline}</Text>

      {period ? (
        <Text className="mt-1 text-base text-typography-900">
          Back {formatAwayDay(getDayAfter(period.endDate), today)}.
        </Text>
      ) : null}

      {errorMessage ? (
        <HStack className="mt-2.5 items-start gap-2" accessibilityRole="alert">
          <Icon as={CircleAlert} size="sm" className="mt-0.5 shrink-0 text-error-700" />

          <Text size="sm" className="text-error-700">
            {errorMessage}
          </Text>
        </HStack>
      ) : null}

      <Button
        variant="outline"
        action="tertiary"
        className="mt-4 w-full rounded-full"
        isDisabled={isBusy}
        onPress={handleRemove}>
        <ButtonText>{getRemoveLabel({ legacyAway: legacyAway && !period, isStarted })}</ButtonText>
      </Button>
    </>
  );

  const editContent = (
    <>
      <Heading size="h4-lg">{isEditing ? 'Change dates' : 'Time away'}</Heading>

      {isStarted && start ? (
        <HStack className="mt-1 items-start gap-1.5">
          <Icon as={Lock} size="xs" className="mt-1 text-typography-700" />

          <Text size="sm" className="flex-1 text-typography-700">
            Started {formatAwayDay(start, today)}. Tap a new last day. Saving replaces your current
            dates.
          </Text>
        </HStack>
      ) : (
        <Text size="sm" className="mt-1 text-typography-700">
          No new inquiries on these days. You can still reply to open conversations.
          {isEditing ? ' Saving replaces your current dates.' : ''}
        </Text>
      )}

      {legacyAway ? (
        <Text size="sm" className="mt-1 text-typography-700">
          Saving dates replaces your current Away setting.
        </Text>
      ) : null}

      <AwayCalendar
        selection={selection}
        today={today}
        locked={isStarted}
        editing={period}
        otherBlocks={otherBlocks}
        onDayPress={(day) => {
          setErrorMessage(null);

          setBlockedNote(null);

          setSelection((current) => applyDayTap(current, day, isStarted));
        }}
        onBlockedDayPress={(day) => {
          setBlockedNote(
            getBlockedDayReason(selection, day, today, isStarted, period, otherBlocks)
          );
        }}
      />

      {/*
        Two lines reserved, as a summary that wrapped would otherwise resize the sheet. A
        refused tap's reason takes the summary's place rather than adding lines below it.
      */}
      <View className="mt-3 min-h-12" accessibilityLiveRegion="polite">
        <Text className="text-base text-typography-900">{blockedNote ?? summary}</Text>
      </View>

      {errorMessage ? (
        <HStack className="mt-2.5 items-start gap-2" accessibilityRole="alert">
          <Icon as={CircleAlert} size="sm" className="mt-0.5 shrink-0 text-error-700" />

          <Text size="sm" className="text-error-700">
            {errorMessage}
          </Text>
        </HStack>
      ) : null}

      <HStack className="flex-wrap items-center">
        <Text size="sm" className="text-typography-600">
          Need more than 30 days away?{' '}
        </Text>

        <Button
          variant="link"
          size="sm"
          className="h-auto min-h-12"
          onPress={() => {
            onClose();

            onContactSupport();
          }}
          {...getAccessibilityProps({ role: 'link', label: 'Message support' })}>
          <ButtonText className="font-medium text-sm text-primary-600 underline data-[hover=true]:text-primary-700">
            Message support
          </ButtonText>
        </Button>
      </HStack>
    </>
  );

  const content = showSummary ? summaryContent : editContent;

  const footer = (
    <VStack className="w-full">
      <Divider />

      <HStack
        className={twJoin('items-center justify-between gap-3 px-4 pt-3', isWeb && 'pb-4')}
        style={isWeb ? undefined : { paddingBottom: Math.max(bottom, 16) + 8 }}>
        {showSummary ? (
          <>
            <Button variant="link" action="primary" isDisabled={isBusy} onPress={onClose}>
              <ButtonText>Close</ButtonText>
            </Button>

            <Button
              className="min-w-[140px] rounded-full"
              isDisabled={isBusy}
              onPress={() => setIsEditingDates(true)}>
              <ButtonText>Change dates</ButtonText>
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="link"
              action="primary"
              isDisabled={isBusy}
              onPress={() => {
                if (!hasPeriodOrLegacy) {
                  onClose();

                  return;
                }

                setSelection(toSelection(period));

                setErrorMessage(null);

                setBlockedNote(null);

                setIsEditingDates(false);
              }}>
              <ButtonText>Cancel</ButtonText>
            </Button>

            <Button
              className="min-w-[140px] rounded-full"
              isDisabled={!start || !end || isBusy || isUnchanged}
              onPress={handleSave}>
              <ButtonText>Save</ButtonText>
            </Button>
          </>
        )}
      </HStack>
    </VStack>
  );

  if (isWeb) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalBackdrop onPress={onClose} className="web:pointer-events-auto" />

        <ModalContent className="max-w-[440px] rounded-2xl p-0">
          <ModalBody className="m-0 px-4 pb-3 pt-6">{content}</ModalBody>

          {footer}
        </ModalContent>
      </Modal>
    );
  }

  // The top inset is read from the hook, not a SafeAreaView, which reports none inside a modal.
  // `statusBarTranslucent` makes Android draw under the status bar so that inset applies there
  // too, and `onRequestClose` is what the Android back button calls.
  return (
    <RNModal
      visible={isOpen}
      animationType="slide"
      presentationStyle="fullScreen"
      statusBarTranslucent
      onRequestClose={onClose}>
      <View className="flex-1 bg-background-0" style={{ paddingTop: top + 8 }}>
        <VStack className="flex-1 px-4 pb-3">{content}</VStack>

        {footer}
      </View>
    </RNModal>
  );
}
